import axios from "axios";
import CacheStorageService from "./cacheStorage.service";

class DigitalVaultService {
    constructor(apiUrl) {
        this.apiUrl = apiUrl;
        this.cache = new CacheStorageService()
        this.cacheUrls = [
            '/categories-by-company',
            '/documents-by-subcategory'
        ]
    }

    /**
     * @author Adriana Fernandez
     * @description Get data for the statistical report
     * @date 07-11-2023
     * @param {String} token
     * @param {String} companyId
     * @returns {Object}
     * @memberof DigitalVaultService
     */
    async getStatisticalReport(token, companyId) {
        const url = new URL(`${this.apiUrl}/digital-vault-dashboard-statistical`);
        if (companyId) url.searchParams.append('companyId', companyId);

        const cache = new CacheStorageService();
        const response = await cache.fetchData(url, token);
        return response.data;
    }

    /**
     * @author Adriana Fernandez
     * @description Get data for the operational report
     * @date 19-10-2023
     * @param {String} token
     * @param {String} timezone
     * @param {String} companyId
     * @returns {Object}
     * @memberof DigitalVaultService
     */
    async getOperationalReport(token, timezone, companyId) {
        const url = new URL(`${this.apiUrl}/digital-vault-dashboard-operational`);
        url.searchParams.append('timezone', timezone);
        if (companyId) url.searchParams.append('companyId', companyId);

        const cache = new CacheStorageService();
        const response = await cache.fetchData(url, token);
        return response.data;
    }

    /**
     * @author Adriana Fernandez
     * @description Get documents (advanced search)
     * @date 10-11-2023
     * @param {String} token
     * @param {String} companyId
     * @param {String} categoryId
     * @param {Boolean} globalSearch
     * @returns {Object}
     * @memberof DigitalVaultService
     */
    async searchDocuments(token, companyId, categoryId, companyHasPermission, categoryHasPermission, globalSearch) {
        const url = new URL(`${this.apiUrl}/vault-document-search`);
        if (!globalSearch) {
            url.searchParams.append('companyId', companyId);
            url.searchParams.append('companyHasPermission', companyHasPermission);
            if (categoryId) {
                url.searchParams.append('categoryId', categoryId);
                url.searchParams.append('categoryHasPermission', categoryHasPermission);
            }
        } else {
            url.searchParams.append('categoryId', categoryId);
            url.searchParams.append('companyId', companyId)
        }

        const cache = new CacheStorageService();
        const response = await cache.fetchData(url, token);
        return response.data;
    }

    /**
    * @author Adriana Fernandez
    * @description Create a document log
    * @date 15-11-2023
    * @param {String} documentId
    * @param {Number} documentActionId
    * @param {String} sending
    * @param {String} sharing
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultService
    */
    async createDocumentLog(documentId, documentActionId, sending, sharing, token) {
        let response = await axios.post(`${this.apiUrl}/create-log`, {
            documentId, documentActionId, sending, sharing
        },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
        return response.data
    }

    /**
     * @author Adriana Fernandez
     * @description Get all deleted documents (papelera de reciclaje)
     * @date 23-11-2023
     * @param {String} token
     * @returns {Object}
     * @memberof DigitalVaultService
     */
    async getDeletedDocuments(token, timezone) {
        const url = new URL(`${this.apiUrl}/digital-vault-bin`);
        url.searchParams.append('timezone', timezone);
        const cache = new CacheStorageService();
        const response = await cache.fetchData(url, token);
        return response.data;
    }

    /**
     * @author Adriana Fernandez
     * @description Get all deleted documents (papelera de reciclaje)
     * @date 23-11-2023
     * @param {String} token
     * @returns {Object}
     * @memberof DigitalVaultService
     */
    async getTrashDocuments(token, timezone) {
        const url = new URL(`${this.apiUrl}/digital-vault-trash`);
        url.searchParams.append('timezone', timezone);
        const cache = new CacheStorageService();
        const response = await cache.fetchData(url, token);
        return response.data;
    }

    /**
    * @author Nestor Sandoval
    * @description Initi the delete workflow for a document.
    * @date 10-09-2025
    * @param {String} token
    * @param {String} documentId
    * @param {Boolean} deleteDocument
    * @returns {Object}
    * @memberof DigitalVaultService
    */
    async startDeleteWorkflow(documentId, deleteDocument, token) {
        const response = await axios.post(
            `${this.apiUrl}/delete-document-workflow-execution`,
            {
                documentId,
                action: "create",
                deleteDocument
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        this.cacheUrls.forEach(url => {
            this.cache.deleteUrlFromCache(url)
        })

        return response.data
    }
    /**
     * @author Nestor Sandoval
     * @description Proceed to the next step in the delete or restore workflow for a document.
     * @date 10-09-2025
     * @param {String} documentId   
     * @param {String} token
     * @param {Boolean} deleteDocument
     */
    async nextDeleteWorkflowStep(documentId, token, deleteDocument) {
        const payload = {
            documentId,
            action: "next",
            deleteDocument
        };

        const response = await axios.post(
            `${this.apiUrl}/delete-document-workflow-execution`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        )

        this.cacheUrls.forEach(url => {
            this.cache.deleteUrlFromCache(url)
        })

        return response.data
    }
}

export { DigitalVaultService as default };